// Daten für Kopf und Widgets der Dokumente (Handoff Dokumente §6.1): Zählabfragen und schmale Selects, parallel.

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../supabase/client';
import { alleSeiten } from '../supabase/alleSeiten';
import { aktenschrankKennzahlen, signaturKennzahlen, type AktenDatei, type SignaturKurz } from './kennzahlen';
import { MASSEN_ABLAGE_SCHWELLE } from './kennzahlen';
import { OFFENE_STATUS } from './ablage';

export interface ZuletztDatei {
  id: string; titel: string | null; datei_name: string | null; kategorie: string | null; created_at: string;
  objekt_id: string | null; ablage_pfad: string | null; gdrive_file_id: string;
  objekt: { objektnummer: string | null } | null;
}

export interface DokumenteKopf {
  ablage: { offen: number; hoch: number };
  signatur: ReturnType<typeof signaturKennzahlen>;
  akten: ReturnType<typeof aktenschrankKennzahlen> & { ohneOrdner: number };
  zuletzt: ZuletztDatei[];
}

export type DokumenteKopfZustand = { status: 'laedt' } | { status: 'fehler'; meldung: string } | { status: 'ok'; kopf: DokumenteKopf };

/** Gemeldet nach Änderungen (Ablage, Signatur, Upload), damit Kopf und Zähler neu laden. */
export const DOKUMENTE_GEAENDERT = 'immo-dokumente-geaendert';
export const meldeDokumenteGeaendert = () => window.dispatchEvent(new CustomEvent(DOKUMENTE_GEAENDERT));

const zahl = (r: { count: number | null; error: { message: string } | null }) => { if (r.error) throw new Error(r.error.message); return r.count ?? 0; };

async function lade(): Promise<DokumenteKopf> {
  const [offen, hoch, sig, dateien, ohneOrdner, zuletzt] = await Promise.all([
    supabase.from('dokument_ablage_vorschlaege').select('id', { count: 'exact', head: true }).in('status', [...OFFENE_STATUS]),
    supabase.from('dokument_ablage_vorschlaege').select('id', { count: 'exact', head: true }).in('status', [...OFFENE_STATUS]).gte('confidence', MASSEN_ABLAGE_SCHWELLE),
    supabase.from('signatur_vorgaenge').select('status, unterzeichner, abgelegt_am').eq('ist_vorlage', false),
    alleSeiten<AktenDatei>((von, bis) => supabase.from('dokumente').select('objekt_id, ablage_pfad')
      .not('gdrive_file_id', 'is', null).eq('is_deleted', false).neq('status', 'Archiviert').order('id').range(von, bis)),
    supabase.from('objekte').select('id', { count: 'exact', head: true }).is('gdrive_folder_id', null).eq('ist_verwaltung', false),
    supabase.from('dokumente').select('id, titel, datei_name, kategorie, created_at, objekt_id, ablage_pfad, gdrive_file_id, objekt:objekt_id(objektnummer)')
      .not('gdrive_file_id', 'is', null).not('objekt_id', 'is', null).eq('is_deleted', false).neq('status', 'Archiviert')
      // interne Ablage (02_Intern/…) liegt nicht im Objekt-Ordner des Aktenschranks
      .not('ablage_pfad', 'like', '%_Intern/%')
      .order('created_at', { ascending: false }).limit(3),
  ]);
  if (sig.error) throw new Error(sig.error.message);
  if (zuletzt.error) throw new Error(zuletzt.error.message);
  return {
    ablage: { offen: zahl(offen), hoch: zahl(hoch) },
    signatur: signaturKennzahlen((sig.data ?? []) as SignaturKurz[]),
    akten: { ...aktenschrankKennzahlen(dateien), ohneOrdner: zahl(ohneOrdner) },
    zuletzt: (zuletzt.data ?? []) as unknown as ZuletztDatei[],
  };
}

export function useDokumenteKopf(): { zustand: DokumenteKopfZustand; neuLaden: () => void } {
  const [zustand, setZustand] = useState<DokumenteKopfZustand>({ status: 'laedt' });
  const [runde, setRunde] = useState(0);
  useEffect(() => {
    let aktiv = true;
    lade().then(kopf => { if (aktiv) setZustand({ status: 'ok', kopf }); })
      .catch((e: unknown) => { if (aktiv) setZustand({ status: 'fehler', meldung: e instanceof Error ? e.message : 'Unbekannter Fehler' }); });
    return () => { aktiv = false; };
  }, [runde]);
  useEffect(() => {
    const neu = () => setRunde(r => r + 1);
    window.addEventListener(DOKUMENTE_GEAENDERT, neu);
    return () => window.removeEventListener(DOKUMENTE_GEAENDERT, neu);
  }, []);
  const neuLaden = useCallback(() => setRunde(r => r + 1), []);
  return { zustand, neuLaden };
}
